"use strict";
/**
 * `validatePassDesign` — pure, blocking-and-warning validation of a `PassDesign`
 * against Apple PassKit constraints (spec §4). The API rejects on any `severity:
 * 'error'`; warnings surface in the API response + future designer UI but never
 * block. No I/O, no throw — returns a flat list of `{ path, message, severity }`.
 *
 * TOTAL over arbitrary input: callers hand this raw JSON.parse output cast to
 * `PassDesign`, so a structural pass type-guards every traversal first and
 * reports malformed shapes as `severity:'error'` findings instead of throwing.
 * Unknown top-level keys BLOCK too — a stray `tripId` / `flight` key on a
 * stored design would flip the `isPassDesign` discriminator and route the pass
 * down the PassViewModel update pipeline. Array walks are capped
 * (`MAX_SCANNED_ITEMS`) so a crafted giant/sparse array cannot burn CPU — and
 * any array LONGER than the cap is itself a blocking error: downstream
 * consumers (designFieldToApple, the Google overlay) iterate every entry, so
 * an array with unscanned entries must never validate clean.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validatePassDesign = validatePassDesign;
const FIELD_GROUPS = ['header', 'primary', 'secondary', 'auxiliary', 'back'];
/**
 * Defensive cap on caller-supplied array walks — far above every Apple limit
 * (max 10 locations, max 5 auxiliary fields), so real designs are unaffected,
 * but a crafted multi-million-entry (or sparse `Array(1e9)`) list cannot spin
 * the validator. The structural pass scans exactly the first MAX_SCANNED_ITEMS
 * entries; the semantic walks below never touch an index past the cap, so an
 * unscanned entry can never reach a `.trim()`/property access. Any array
 * LONGER than the cap is a blocking finding (see `overCapMessage`): consumers
 * iterate ALL entries, so junk hiding past the cap must not validate clean.
 */
const MAX_SCANNED_ITEMS = 100;
/** Blocking message for a capped array walk that could not scan every entry. */
const OVER_CAP_MESSAGE = `at most ${String(MAX_SCANNED_ITEMS)} entries are supported (validation scan cap)`;
/** Yields every field with its `fields.<group>[i]` path prefix (capped walk). */
function* eachField(design) {
    for (const group of FIELD_GROUPS) {
        const list = design.fields[group];
        if (list === undefined)
            continue;
        const limit = Math.min(list.length, MAX_SCANNED_ITEMS);
        for (let index = 0; index < limit; index++) {
            const field = list[index];
            if (field === undefined)
                continue; // noUncheckedIndexedAccess
            yield { group, index, field };
        }
    }
}
// ─── Structural pass (total-function contract) ───────────────────────────────
function isPlainObject(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
/** Whitelist = the `PassDesign` type's own keys — anything else is rejected. */
const TOP_LEVEL_KEYS = new Set([
    'style',
    'images',
    'colors',
    'logoText',
    'transitType',
    'fields',
    'barcode',
    'semantics',
    'relevance',
    'serviceURLs',
    'featuredActions',
]);
const APPLE_STYLE_VALUES = new Set([
    'boardingPass',
    'coupon',
    'eventTicket',
    'generic',
    'storeCard',
]);
const IMAGE_SLOTS = ['icon', 'logo', 'footer', 'strip', 'thumbnail', 'background'];
const COLOR_SLOTS = ['background', 'foreground', 'label'];
// Enum value lists mirror the `PassDesignField` / `PassDesignBarcode` types in
// passDesign.ts — a crafted save with a non-enum value would otherwise validate
// clean here and produce a pass.json iOS silently refuses.
const FIELD_TEXT_ALIGNMENT_VALUES = new Set([
    'PKTextAlignmentLeft',
    'PKTextAlignmentCenter',
    'PKTextAlignmentRight',
    'PKTextAlignmentNatural',
]);
const FIELD_DATE_STYLE_VALUES = new Set([
    'PKDateStyleNone',
    'PKDateStyleShort',
    'PKDateStyleMedium',
    'PKDateStyleLong',
    'PKDateStyleFull',
]);
const FIELD_NUMBER_STYLE_VALUES = new Set([
    'PKNumberStyleDecimal',
    'PKNumberStylePercent',
    'PKNumberStyleScientific',
    'PKNumberStyleSpellOut',
]);
const BARCODE_FORMAT_VALUES = new Set([
    'PKBarcodeFormatQR',
    'PKBarcodeFormatPDF417',
    'PKBarcodeFormatAztec',
    'PKBarcodeFormatCode128',
]);
/**
 * Structural check of one field entry's OPTIONAL properties (the required
 * string `key`/`value` are checked by the caller). Returns the first problem
 * message, or null when structurally sound.
 */
function fieldEntryIssue(entry) {
    for (const prop of ['label', 'changeMessage', 'currencyCode']) {
        const value = entry[prop];
        if (value !== undefined && typeof value !== 'string') {
            return `malformed: ${prop} must be a string`;
        }
    }
    const alignment = entry['textAlignment'];
    if (alignment !== undefined &&
        (typeof alignment !== 'string' || !FIELD_TEXT_ALIGNMENT_VALUES.has(alignment))) {
        return 'malformed: textAlignment must be a PKTextAlignment* value';
    }
    for (const prop of ['dateStyle', 'timeStyle']) {
        const value = entry[prop];
        if (value !== undefined && (typeof value !== 'string' || !FIELD_DATE_STYLE_VALUES.has(value))) {
            return `malformed: ${prop} must be a PKDateStyle* value`;
        }
    }
    const numberStyle = entry['numberStyle'];
    if (numberStyle !== undefined &&
        (typeof numberStyle !== 'string' || !FIELD_NUMBER_STYLE_VALUES.has(numberStyle))) {
        return 'malformed: numberStyle must be a PKNumberStyle* value';
    }
    return null;
}
/**
 * Type-guards everything the semantic checks traverse. Returns malformed /
 * unknown-key findings; when ANY are present the caller returns them alone —
 * the semantic checks assume a structurally sound design (they would otherwise
 * throw on the junk), and the API rejects on these findings anyway.
 */
function structuralFindings(input) {
    if (!isPlainObject(input)) {
        return [{ path: '', message: 'malformed design: not an object', severity: 'error' }];
    }
    const errors = [];
    const bad = (path, message) => {
        errors.push({ path, message, severity: 'error' });
    };
    for (const key of Object.keys(input)) {
        if (!TOP_LEVEL_KEYS.has(key))
            bad(key, 'unknown top-level key (not part of PassDesign)');
    }
    if (typeof input.style !== 'string' || !APPLE_STYLE_VALUES.has(input.style)) {
        bad('style', 'malformed: style must be one of boardingPass, coupon, eventTicket, generic, storeCard');
    }
    if (!isPlainObject(input.images)) {
        bad('images', 'malformed: images must be an object');
    }
    else {
        for (const slot of IMAGE_SLOTS) {
            const img = input.images[slot];
            if (img === undefined)
                continue;
            if (!isPlainObject(img) || typeof img.s3KeyBase !== 'string') {
                bad(`images.${slot}`, 'malformed: image slot must be an object with a string s3KeyBase');
            }
        }
    }
    if (input.colors !== undefined) {
        if (!isPlainObject(input.colors)) {
            bad('colors', 'malformed: colors must be an object');
        }
        else {
            for (const slot of COLOR_SLOTS) {
                const value = input.colors[slot];
                if (value !== undefined && typeof value !== 'string') {
                    bad(`colors.${slot}`, 'malformed: colour must be a string');
                }
            }
        }
    }
    if (input.logoText !== undefined && typeof input.logoText !== 'string') {
        bad('logoText', 'malformed: logoText must be a string');
    }
    if (input.transitType !== undefined && typeof input.transitType !== 'string') {
        bad('transitType', 'malformed: transitType must be a string');
    }
    if (!isPlainObject(input.fields)) {
        bad('fields', 'malformed: fields must be an object');
    }
    else {
        for (const group of FIELD_GROUPS) {
            const list = input.fields[group];
            if (list === undefined)
                continue;
            if (!Array.isArray(list)) {
                bad(`fields.${group}`, 'malformed: field group must be an array');
                continue;
            }
            if (list.length > MAX_SCANNED_ITEMS) {
                // Unscanned entries past the cap could be junk that crashes downstream
                // consumers (they iterate ALL entries) — block instead of trusting them.
                bad(`fields.${group}`, OVER_CAP_MESSAGE);
            }
            const limit = Math.min(list.length, MAX_SCANNED_ITEMS);
            for (let i = 0; i < limit; i++) {
                const entry = list[i];
                if (!isPlainObject(entry) ||
                    typeof entry.key !== 'string' ||
                    typeof entry.value !== 'string') {
                    // One finding per group, not one per junk entry — a giant junk
                    // array yields one line instead of thousands.
                    bad(`fields.${group}[${String(i)}]`, 'malformed: field must be an object with string key and value');
                    break;
                }
                const issue = fieldEntryIssue(entry);
                if (issue !== null) {
                    bad(`fields.${group}[${String(i)}]`, issue);
                    break;
                }
            }
        }
    }
    if (input.barcode !== undefined) {
        const barcode = input.barcode;
        if (!isPlainObject(barcode) ||
            typeof barcode.format !== 'string' ||
            typeof barcode.message !== 'string') {
            bad('barcode', 'malformed: barcode must be an object with string format and message');
        }
        else {
            if (!BARCODE_FORMAT_VALUES.has(barcode.format)) {
                bad('barcode.format', 'malformed: barcode format must be a PKBarcodeFormat* value');
            }
            if (barcode.altText !== undefined && typeof barcode.altText !== 'string') {
                bad('barcode.altText', 'malformed: altText must be a string');
            }
        }
    }
    if (input.semantics !== undefined && !isPlainObject(input.semantics)) {
        bad('semantics', 'malformed: semantics must be an object');
    }
    if (input.relevance !== undefined) {
        if (!isPlainObject(input.relevance)) {
            bad('relevance', 'malformed: relevance must be an object');
        }
        else if (input.relevance.locations !== undefined) {
            const locations = input.relevance.locations;
            if (!Array.isArray(locations)) {
                bad('relevance.locations', 'malformed: locations must be an array');
            }
            else {
                if (locations.length > MAX_SCANNED_ITEMS) {
                    bad('relevance.locations', OVER_CAP_MESSAGE);
                }
                const limit = Math.min(locations.length, MAX_SCANNED_ITEMS);
                for (let i = 0; i < limit; i++) {
                    const entry = locations[i];
                    if (!isPlainObject(entry)) {
                        bad(`relevance.locations[${String(i)}]`, 'malformed: location must be an object');
                        break;
                    }
                }
            }
        }
    }
    if (input.serviceURLs !== undefined) {
        if (!isPlainObject(input.serviceURLs)) {
            bad('serviceURLs', 'malformed: serviceURLs must be an object');
        }
        else {
            for (const [key, value] of Object.entries(input.serviceURLs)) {
                if (value !== undefined && typeof value !== 'string') {
                    bad(`serviceURLs.${key}`, 'malformed: URL must be a string');
                }
            }
        }
    }
    if (input.featuredActions !== undefined) {
        const actions = input.featuredActions;
        if (!Array.isArray(actions)) {
            bad('featuredActions', 'malformed: featuredActions must be an array');
        }
        else {
            if (actions.length > MAX_SCANNED_ITEMS) {
                bad('featuredActions', OVER_CAP_MESSAGE);
            }
            const limit = Math.min(actions.length, MAX_SCANNED_ITEMS);
            for (let i = 0; i < limit; i++) {
                const entry = actions[i];
                if (!isPlainObject(entry) ||
                    typeof entry.label !== 'string' ||
                    typeof entry.url !== 'string') {
                    bad(`featuredActions[${String(i)}]`, 'malformed: featured action must be an object with string label and url');
                    break;
                }
            }
        }
    }
    return errors;
}
// Wallet requires ISO-8601 date-times WITH an explicit offset — an offset-less
// value renders host-TZ-dependently and iOS may silently refuse the pass.
// Seconds/milliseconds optional; offset `Z` or `±hh:mm`. Date.parse backstops
// calendar-impossible values that match the shape (e.g. month 13).
const ISO_DATETIME_WITH_TZ_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:\d{2})$/;
function isIsoDateTimeWithTz(value) {
    return ISO_DATETIME_WITH_TZ_RE.test(value) && !Number.isNaN(Date.parse(value));
}
// Mirrors wallet-apple/passJsonBuilder.ts so a design that validates here also
// normalizes there. A colour is `#hhhhhh` OR `rgb(r,g,b)` with components 0-255.
const RGB_RE = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;
function isValidColor(color) {
    const trimmed = color.trim();
    if (HEX_RE.test(trimmed))
        return true;
    const rgb = RGB_RE.exec(trimmed);
    if (rgb === null)
        return false;
    const [, rs = '', gs = '', bs = ''] = rgb;
    return parseInt(rs, 10) <= 255 && parseInt(gs, 10) <= 255 && parseInt(bs, 10) <= 255;
}
/** Parse `#hhhhhh` or `rgb(r,g,b)` to `[r,g,b]`, or null if unparseable. */
function parseRgb(color) {
    const trimmed = color.trim();
    const hex = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(trimmed);
    if (hex !== null) {
        const [, r = '', g = '', b = ''] = hex;
        return [parseInt(r, 16), parseInt(g, 16), parseInt(b, 16)];
    }
    const rgb = RGB_RE.exec(trimmed);
    if (rgb !== null) {
        const [, r = '', g = '', b = ''] = rgb;
        return [parseInt(r, 10), parseInt(g, 10), parseInt(b, 10)];
    }
    return null;
}
/** Rec. 601 relative luminance, 0-255 scale. */
function luminance([r, g, b]) {
    return 0.299 * r + 0.587 * g + 0.114 * b;
}
// Actionable URLs (serviceURLs.* + featuredActions[].url) are surfaced by the
// pass and, in the designer preview, could execute in a browser context — so
// only these safe schemes are allowed. Anything else (javascript:, data:,
// vbscript:, file:, http:, relative, unparseable) is a blocking error.
const ALLOWED_URL_SCHEMES = new Set(['https:', 'mailto:', 'tel:']);
function hasAllowedScheme(url) {
    let parsed;
    try {
        parsed = new URL(url);
    }
    catch {
        return false; // relative / unparseable
    }
    return ALLOWED_URL_SCHEMES.has(parsed.protocol);
}
function validatePassDesign(design) {
    // ── Structural pass first — see structuralFindings. On ANY malformed /
    // unknown-key finding return early: every semantic traversal below relies
    // on the guarded shapes.
    const structural = structuralFindings(design);
    if (structural.length > 0)
        return structural;
    const errors = [];
    // ── Required: icon ──────────────────────────────────────────────────────
    if (design.images.icon === undefined) {
        errors.push({ path: 'images.icon', message: 'icon image is required', severity: 'error' });
    }
    // ── Required: non-empty field key + value ───────────────────────────────
    for (const { group, index, field } of eachField(design)) {
        if (field.key.trim() === '') {
            errors.push({
                path: `fields.${group}[${String(index)}].key`,
                message: 'field key must be non-empty',
                severity: 'error',
            });
        }
        if (field.value.trim() === '') {
            errors.push({
                path: `fields.${group}[${String(index)}].value`,
                message: 'field value must be non-empty',
                severity: 'error',
            });
        }
    }
    // ── Pass-wide key uniqueness (Apple constraint) ─────────────────────────
    // Apple diffs and de-dupes fields by key across EVERY group; a repeated key
    // makes the pass invalid on-device ("more than one field has the key
    // 'lounge'" — live incident 2026-08-28; warning today, hard error on newer
    // iOS). Warn, never block: legacy saved designs carry seed-era repeats and
    // must keep re-rendering (wallet-apple's render renames the later copy).
    const seenFieldKeys = new Map();
    for (const { group, index, field } of eachField(design)) {
        const key = field.key.trim();
        if (key === '')
            continue; // already an error above
        const firstGroup = seenFieldKeys.get(key);
        if (firstGroup === undefined) {
            seenFieldKeys.set(key, group);
            continue;
        }
        errors.push({
            path: `fields.${group}[${String(index)}].key`,
            message: `duplicate field key '${key}' (already used in ${firstGroup}) — Apple requires keys unique across the whole pass; the Apple render de-duplicates legacy designs, but new designs must not repeat keys`,
            severity: 'warn',
        });
    }
    // ── Required: barcode format + message ──────────────────────────────────
    if (design.barcode !== undefined) {
        if (design.barcode.message.trim() === '') {
            errors.push({
                path: 'barcode.message',
                message: 'barcode message must be non-empty',
                severity: 'error',
            });
        }
    }
    // ── Per-style image support ─────────────────────────────────────────────
    const { style, images } = design;
    if (images.footer !== undefined && style !== 'boardingPass') {
        errors.push({
            path: 'images.footer',
            message: 'footer image is only supported on boardingPass',
            severity: 'error',
        });
    }
    if (images.strip !== undefined &&
        style !== 'coupon' &&
        style !== 'storeCard' &&
        style !== 'eventTicket') {
        errors.push({
            path: 'images.strip',
            message: 'strip image is only supported on coupon, storeCard, eventTicket',
            severity: 'error',
        });
    }
    if (images.thumbnail !== undefined && style !== 'generic' && style !== 'eventTicket') {
        errors.push({
            path: 'images.thumbnail',
            message: 'thumbnail image is only supported on generic, eventTicket',
            severity: 'error',
        });
    }
    if (images.background !== undefined && style !== 'eventTicket') {
        errors.push({
            path: 'images.background',
            message: 'background image is only supported on eventTicket',
            severity: 'error',
        });
    }
    if (style === 'eventTicket' &&
        images.strip !== undefined &&
        (images.background !== undefined || images.thumbnail !== undefined)) {
        errors.push({
            path: 'images.strip',
            message: 'eventTicket cannot combine strip with background or thumbnail',
            severity: 'error',
        });
    }
    // ── Field counts ────────────────────────────────────────────────────────
    const count = (group) => design.fields[group]?.length ?? 0;
    if (count('header') > 3) {
        errors.push({
            path: 'fields.header',
            message: 'header supports at most 3 fields',
            severity: 'error',
        });
    }
    const maxPrimary = style === 'boardingPass' ? 2 : 1;
    if (count('primary') > maxPrimary) {
        errors.push({
            path: 'fields.primary',
            message: `${style} supports at most ${String(maxPrimary)} primary field${maxPrimary === 1 ? '' : 's'}`,
            severity: 'error',
        });
    }
    // Square barcodes (QR / Aztec) crowd out the lower fields on the flat styles.
    const squareBarcode = design.barcode !== undefined &&
        (design.barcode.format === 'PKBarcodeFormatQR' ||
            design.barcode.format === 'PKBarcodeFormatAztec');
    const flatSquareStyle = style === 'coupon' || style === 'storeCard' || style === 'generic' || style === 'eventTicket';
    if (squareBarcode && flatSquareStyle && count('secondary') + count('auxiliary') > 4) {
        errors.push({
            path: 'fields.secondary',
            message: 'with a square barcode, secondary + auxiliary must be at most 4 combined',
            severity: 'error',
        });
    }
    if (style === 'boardingPass' && count('auxiliary') > 5) {
        errors.push({
            path: 'fields.auxiliary',
            message: 'boardingPass supports at most 5 auxiliary fields',
            severity: 'error',
        });
    }
    // ── transitType only on boardingPass ────────────────────────────────────
    if (design.transitType !== undefined && style !== 'boardingPass') {
        errors.push({
            path: 'transitType',
            message: 'transitType is only supported on boardingPass',
            severity: 'error',
        });
    }
    // ── Colour format ───────────────────────────────────────────────────────
    if (design.colors !== undefined) {
        for (const slot of ['background', 'foreground', 'label']) {
            const value = design.colors[slot];
            if (value !== undefined && !isValidColor(value)) {
                errors.push({
                    path: `colors.${slot}`,
                    message: 'colour must be "#hhhhhh" or "rgb(r, g, b)" with components 0-255',
                    severity: 'error',
                });
            }
        }
    }
    // ── Geofence: at most 10 locations ──────────────────────────────────────
    if (design.relevance?.locations !== undefined && design.relevance.locations.length > 10) {
        errors.push({
            path: 'relevance.locations',
            message: 'at most 10 locations are supported',
            severity: 'error',
        });
    }
    // ── Relevance values: real dates + real coordinates ─────────────────────
    // These flow verbatim into pass.json (buildLivePatch forwards `relevance`),
    // and iOS silently refuses a pass with a bad relevantDate / coordinate — so
    // a design must only validate when Wallet would accept the minted pass.
    if (design.relevance !== undefined) {
        for (const slot of ['relevantDate', 'expirationDate']) {
            const value = design.relevance[slot];
            if (value !== undefined && (typeof value !== 'string' || !isIsoDateTimeWithTz(value))) {
                errors.push({
                    path: `relevance.${slot}`,
                    message: 'must be an ISO-8601 date-time with a timezone (e.g. 2026-05-01T08:00:00Z)',
                    severity: 'error',
                });
            }
        }
        const maxDistance = design.relevance.maxDistance;
        if (maxDistance !== undefined &&
            (typeof maxDistance !== 'number' || !Number.isFinite(maxDistance) || maxDistance <= 0)) {
            errors.push({
                path: 'relevance.maxDistance',
                message: 'maxDistance must be a positive finite number (metres)',
                severity: 'error',
            });
        }
        const locations = design.relevance.locations ?? [];
        const locationLimit = Math.min(locations.length, MAX_SCANNED_ITEMS);
        for (let i = 0; i < locationLimit; i++) {
            const location = locations[i];
            if (location === undefined)
                continue; // noUncheckedIndexedAccess
            const lat = location.latitude;
            const lon = location.longitude;
            if (typeof lat !== 'number' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
                errors.push({
                    path: `relevance.locations[${String(i)}].latitude`,
                    message: 'latitude must be a finite number between -90 and 90',
                    severity: 'error',
                });
            }
            if (typeof lon !== 'number' || !Number.isFinite(lon) || lon < -180 || lon > 180) {
                errors.push({
                    path: `relevance.locations[${String(i)}].longitude`,
                    message: 'longitude must be a finite number between -180 and 180',
                    severity: 'error',
                });
            }
        }
    }
    // ── Field number/date: currencyCode XOR numberStyle ─────────────────────
    for (const { group, index, field } of eachField(design)) {
        if (field.currencyCode !== undefined && field.numberStyle !== undefined) {
            errors.push({
                path: `fields.${group}[${String(index)}]`,
                message: 'currencyCode and numberStyle are mutually exclusive',
                severity: 'error',
            });
        }
    }
    // ── WARN: low contrast (fg/label vs bg) ─────────────────────────────────
    const bg = design.colors?.background !== undefined ? parseRgb(design.colors.background) : null;
    if (bg !== null) {
        for (const slot of ['foreground', 'label']) {
            const value = design.colors?.[slot];
            const fg = value !== undefined ? parseRgb(value) : null;
            if (fg !== null && Math.abs(luminance(fg) - luminance(bg)) < 40) {
                errors.push({
                    path: `colors.${slot}`,
                    message: 'low contrast against the background colour',
                    severity: 'warn',
                });
            }
        }
    }
    // ── WARN: relevantDate ignored on coupon / storeCard ────────────────────
    if (design.relevance?.relevantDate !== undefined &&
        (style === 'coupon' || style === 'storeCard')) {
        errors.push({
            path: 'relevance.relevantDate',
            message: 'relevantDate is ignored by iOS on coupon and storeCard',
            severity: 'warn',
        });
    }
    // ── URL-scheme allowlist (serviceURLs.* + featuredActions[].url) ─────────
    if (design.serviceURLs !== undefined) {
        for (const [key, value] of Object.entries(design.serviceURLs)) {
            if (value !== undefined && !hasAllowedScheme(value)) {
                errors.push({
                    path: `serviceURLs.${key}`,
                    message: 'URL scheme not allowed (use https)',
                    severity: 'error',
                });
            }
        }
    }
    if (design.featuredActions !== undefined) {
        const actionLimit = Math.min(design.featuredActions.length, MAX_SCANNED_ITEMS);
        for (let i = 0; i < actionLimit; i++) {
            const action = design.featuredActions[i];
            if (action !== undefined && !hasAllowedScheme(action.url)) {
                errors.push({
                    path: `featuredActions[${String(i)}].url`,
                    message: 'URL scheme not allowed (use https)',
                    severity: 'error',
                });
            }
        }
    }
    // ── WARN: featuredActions only render on generic ────────────────────────
    if (design.featuredActions !== undefined &&
        design.featuredActions.length > 0 &&
        style !== 'generic') {
        errors.push({
            path: 'featuredActions',
            message: 'featuredActions only render on the generic style',
            severity: 'warn',
        });
    }
    return errors;
}
//# sourceMappingURL=validatePassDesign.js.map